import * as React from 'react';
import { ChevronRight, Home } from 'lucide-react';

import { cn } from '../lib/utils';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  showHome?: boolean;
  /**
   * Collapse the middle of a long trail into an ellipsis once the rendered
   * item count (including Home) exceeds this number, keeping the first crumb
   * and the last `maxItems - 1` crumbs. Undefined (default) renders every
   * item — fully behavior-neutral for existing consumers. Use it on deep
   * hierarchies / narrow viewports so the trail never overflows its row.
   */
  maxItems?: number;
}

/** Sentinel inserted in place of the collapsed middle crumbs. */
const ELLIPSIS: unique symbol = Symbol('breadcrumb-ellipsis');
type RenderEntry = BreadcrumbItem | typeof ELLIPSIS;

/**
 * Breadcrumb navigation showing page hierarchy.
 * The last item is rendered as non-interactive current-page text.
 */
export const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  (
    {
      items,
      separator = <ChevronRight className="h-4 w-4 text-muted-foreground" />,
      showHome = false,
      maxItems,
      className,
      ...props
    },
    ref
  ) => {
    if (items.length === 0) return null;

    const allItems: BreadcrumbItem[] = showHome
      ? [{ label: 'Home', href: '/', icon: Home }, ...items]
      : items;

    // Middle-collapse: first crumb + ellipsis + last (maxItems - 1) crumbs.
    const entries: RenderEntry[] =
      maxItems != null && maxItems >= 2 && allItems.length > maxItems
        ? [...allItems.slice(0, 1), ELLIPSIS, ...allItems.slice(allItems.length - (maxItems - 1))]
        : allItems;

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('flex items-center', className)}
        {...props}
      >
        <ol className="flex items-center gap-1.5 text-sm">
          {entries.map((entry, index) => {
            if (entry === ELLIPSIS) {
              return (
                <li key="ellipsis" className="flex items-center gap-1.5">
                  <span
                    className="px-0.5 text-muted-foreground"
                    aria-hidden="true"
                  >
                    …
                  </span>
                  {separator}
                </li>
              );
            }
            const item = entry;
            const isLast = index === entries.length - 1;
            const Icon = item.icon;
            const content = (
              <span className="inline-flex max-w-[12rem] items-center gap-1 truncate">
                {Icon && <Icon className="h-4 w-4 shrink-0" />}
                <span className="truncate">{item.label}</span>
              </span>
            );
            return (
              <li key={index} className="flex min-w-0 items-center gap-1.5">
                {isLast ? (
                  <span
                    className="font-medium text-foreground"
                    aria-current="page"
                  >
                    {content}
                  </span>
                ) : item.href ? (
                  <a
                    href={item.href}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {content}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={item.onClick}
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {content}
                  </button>
                )}
                {!isLast && separator}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  }
);

Breadcrumb.displayName = 'Breadcrumb';
